'use client'

import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { type ReactNode } from 'react'

import useApiClient from '@/lib/apis/clients/useApiClient'
import { GetAvailableMobileLoyaltyReward } from '@/lib/apis/loyalty-rewards'
import type {
  AvailableMobileLoyaltyRewardResponse,
  MobileLoyaltyReward,
} from '@/lib/apis/types'

type TreatmentTarget = {
  consultationType?: never
  treatmentType: 'ed' | 'pe' | 'mhl' | 'wl'
}

export function useWebLoyaltyReward({
  enabled,
  baseAmountCents,
  ...target
}: TreatmentTarget & {
  enabled: boolean
  baseAmountCents?: number | null
}) {
  const client = useApiClient()
  const params = {
    ...target,
    baseAmountCents,
    platform: 'WEB' as const,
  } as GetAvailableMobileLoyaltyReward.Params

  return useQuery({
    ...GetAvailableMobileLoyaltyReward.query(client, params),
    enabled,
    staleTime: 30_000,
  })
}

export function LoyaltyRewardBanner({
  response,
  className,
}: {
  response?: AvailableMobileLoyaltyRewardResponse
  className?: string
}) {
  const reward = response?.reward
  if (!reward) return null

  return (
    <aside
      aria-label="Available reward"
      className={clsx(
        'mx-auto mb-7 flex w-full items-center gap-4 rounded-[18px] bg-[#DFF2EC] px-[18px] py-[18px] sm:gap-6 sm:px-6 sm:py-5',
        className,
      )}
      data-testid="quiz-reward-banner"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
        <strong className="text-[14.5px] font-semibold leading-5 text-[#0B0B0F] sm:text-[15px]">
          {reward.title}
        </strong>
        <span className="text-[13.5px] leading-5 text-[#53685F] sm:text-sm">
          {reward.description ?? 'Your reward will be applied at payment.'}
        </span>
      </div>
      <strong className="shrink-0 font-display text-[20px] font-bold leading-7 tracking-[-0.02em] text-[#1F8A70] sm:text-[22px]">
        {formatRewardValue(reward)} off
      </strong>
    </aside>
  )
}

export function RewardedPrice({
  response,
  fallback,
  layout = 'stacked',
}: {
  response?: AvailableMobileLoyaltyRewardResponse
  fallback: ReactNode
  layout?: 'inline' | 'stacked'
}) {
  const quote = response?.estimatedQuote
  if (!quote || quote.rewardAmountCents <= 0) return <>{fallback}</>

  return (
    <span
      className={clsx(
        'flex items-end gap-0.5',
        layout === 'inline' ? 'flex-row items-baseline gap-2' : 'flex-col',
      )}
    >
      <span className="text-sm font-medium leading-5 text-gray-400 line-through">
        {formatCents(quote.originalAmountCents)}
      </span>
      <span
        className={clsx(
          layout === 'inline' && 'font-semibold text-rich-black',
        )}
      >
        {formatCents(quote.payableAmountCents)}
      </span>
    </span>
  )
}

export function RewardSaving({
  response,
  className,
}: {
  response?: AvailableMobileLoyaltyRewardResponse
  className?: string
}) {
  const quote = response?.estimatedQuote
  if (!quote || quote.rewardAmountCents <= 0) return null

  return (
    <div
      className={clsx(
        'mt-1 flex items-center gap-1.5 text-[13px] font-medium leading-[18px] text-green-700',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="size-1.5 shrink-0 rounded-full bg-green-600"
      />
      <span>
        {formatCompactCents(quote.rewardAmountCents)} loyalty reward applied
      </span>
    </div>
  )
}

export function rewardId(
  response: AvailableMobileLoyaltyRewardResponse | undefined,
) {
  return response?.reward?.id ?? null
}

function formatRewardValue(reward: MobileLoyaltyReward) {
  if (reward.discountType === 'PERCENTAGE_BPS') {
    const percentage = reward.discountValue / 100
    return `${Number.isInteger(percentage) ? percentage : percentage.toFixed(2)}%`
  }

  const amount = formatCents(reward.discountValue)
  return amount.endsWith('.00') ? amount.slice(0, -3) : amount
}

function formatCents(cents: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(cents / 100)
}

function formatCompactCents(cents: number) {
  return formatCents(cents).replace(/\.00$/, '')
}
